import { flowRight } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import PostListItemHeader from '../post-list-item-header';
import PostTitle from '../post-title';
import ContentExcerpt from '../content-excerpt';
import PostFooter from '../../../app/components/post-footer';
import PostListItemCover from '../../../app/components/post-list-item-cover';
import { getFontDisplayName } from '../../selectors/app-settings-selectors';
import getElementType from '../../../app/services/get-element-type';
import { POST_TITLE_FONT } from '../../constants/wix-params';
import { getPostCover } from '../../../app/services/get-post-cover';
import { getLineCounts } from '../../services/layout-config';
import { HorizontalSeparatorForPostCard } from '../../../app/components/separator';
import withFontClassName from '../../../app/hoc/with-font-class-name';
import withNavigation from '../../../app/hoc/with-navigation';
import withFeedMetadataSettings from '../../../app/hoc/with-feed-metadata-settings';
import { LAYOUT_NAME_SIDE_BY_SIDE, LAYOUT_NAME_MOBILE } from '@wix/communities-blog-client-common';
import styles from './post-list-item.scss';

export const PostListItem = ({
  layoutName,
  isLayoutTextOnImage,
  post,
  onLikeClick,
  itemConfig,
  borderWidth,
  titleFontClassName,
  contentFontClassName,
  navigation: { Link },
  isMetadataFooterVisible,
  isMetadataHeaderVisible,
  index,
  titleElementType,
}) => {
  const { shouldRender: withCover } = getPostCover(post);
  const lineCounts = getLineCounts(itemConfig, withCover);
  const postLink = `/${post.slug}`;
  const autoFit = layoutName === LAYOUT_NAME_SIDE_BY_SIDE && withCover;
  const withoutFooter = !isMetadataFooterVisible;
  const withoutHeaderMetadata = !isMetadataHeaderVisible;
  const isMobileLayout = layoutName === LAYOUT_NAME_MOBILE;

  const containerClassName = classNames(
    styles.container,
    styles[layoutName],
    withCover && styles.withCover,
    withoutFooter && styles.withoutFooter,
    contentFontClassName,
    'blog-text-color',
    'blog-card-background-color',
    'blog-card-border-color',
    'post-list-item',
    !isLayoutTextOnImage && 'blog-hover-container',
    { [styles.mobile]: layoutName === LAYOUT_NAME_MOBILE },
    isMobileLayout && index > 0 && styles.newPadding,
  );
  const contentWrapperClassName = classNames(
    styles.contentWrapper,
    styles[layoutName],
    withCover && styles.withCover,
    withoutFooter && styles.withoutFooter,
    isLayoutTextOnImage && 'blog-post-image-overlay-background-color',
    isMobileLayout ? styles.newPadding : styles.boxShadow,
  );
  const linkClassName = classNames(
    styles.textWrapper,
    styles[layoutName],
    withCover && styles.withCover,
    withoutHeaderMetadata && styles.withoutHeaderMetadata,
    post.isPinned && styles.withIcons,
  );
  const titleClassName = classNames(
    styles.title,
    titleFontClassName,
    styles[layoutName],
    withCover && styles.withCover,
    withoutFooter && styles.withoutFooter,
    withoutHeaderMetadata && styles.withoutHeaderMetadata,
    post.isPinned && styles.withIcons,
  );
  const contentClassName = classNames(
    styles.content,
    styles[layoutName],
    contentFontClassName,
    withoutHeaderMetadata && styles.withoutHeaderMetadata,
    withoutFooter && styles.withoutFooter,
  );

  const renderCover = () => {
    const coverProps = {
      post,
      postLink,
      type: layoutName,
      useAlternateDimesions: withoutFooter,
    };

    if (layoutName === LAYOUT_NAME_SIDE_BY_SIDE) {
      coverProps.withoutContainerStyle = true;
    }

    return (
      <PostListItemCover
        canPlayVideo
        videoClassName={styles.videoEmbed}
        imageClassName={styles.image}
        useAlternateDimesions={withoutFooter}
        {...coverProps}
      />
    );
  };

  return (
    <article className={containerClassName} tabIndex="0" style={{ borderWidth }} data-hook="post-list-item">
      {withCover && renderCover()}
      <div className={contentWrapperClassName}>
        {itemConfig.displayHeader && (
          <div className={classNames(styles.header, contentFontClassName)}>
            <PostListItemHeader
              type={layoutName}
              post={post}
              showProfileImage={!isLayoutTextOnImage}
              showMoreButton={false}
            />
          </div>
        )}
        <Link to={postLink} className={linkClassName} addHoverClasses={false}>
          <div className={titleClassName} data-hook="post-title">
            <PostTitle
              titleElementType={titleElementType}
              type={layoutName}
              title={post.title}
              textColorClassName="blog-post-title-color"
              lineCount={lineCounts.title}
            />
          </div>
          {lineCounts.description ? (
            <div className={contentClassName} data-hook="post-description">
              <ContentExcerpt
                type={layoutName}
                content={post.content}
                excerpt={post.excerpt}
                lineCount={lineCounts.description}
                autoFit={autoFit}
              />
            </div>
          ) : null}
        </Link>
        {isMetadataFooterVisible && (
          <div className={classNames(styles.footer, styles[layoutName], !withCover && styles.withoutCover)}>
            {!isMobileLayout && (
              <HorizontalSeparatorForPostCard className={classNames(styles.separator, styles[layoutName])} />
            )}
            <div className={contentFontClassName}>
              <PostFooter
                post={post}
                postLink={postLink}
                onLikeClick={onLikeClick}
                displayIcons={itemConfig.displayFooterIcons}
              />
            </div>
          </div>
        )}
        {isMobileLayout && (
          <HorizontalSeparatorForPostCard
            className={classNames(styles.separator, styles.updatedMobileSeparator, styles[layoutName])}
          />
        )}
      </div>
    </article>
  );
};

PostListItem.propTypes = {
  onLikeClick: PropTypes.func.isRequired,
  post: PropTypes.object,
  layoutName: PropTypes.string.isRequired,
  isLayoutTextOnImage: PropTypes.bool.isRequired,
  itemConfig: PropTypes.object.isRequired,
  borderWidth: PropTypes.number.isRequired,
  titleFontClassName: PropTypes.string.isRequired,
  contentFontClassName: PropTypes.string.isRequired,
  navigation: PropTypes.object,
  isMetadataFooterVisible: PropTypes.bool,
  isMetadataHeaderVisible: PropTypes.bool,
  index: PropTypes.number,
  titleElementType: PropTypes.string.isRequired,
};

PostListItem.defaultProps = {
  isMetadataFooterVisible: true,
  isMetadataHeaderVisible: true,
};

const mapStateToProps = (state) => ({
  titleElementType: getElementType(getFontDisplayName(state, POST_TITLE_FONT), 'h2'),
});

// prettier-ignore
export default flowRight(
  connect(mapStateToProps),
  withNavigation,
  withFontClassName,
  withFeedMetadataSettings,
)(PostListItem);
